import _ from 'underscore';
import qs from 'qs';
import i18next from 'i18next';
import fileDownload from 'react-file-download';
import request from 'superagent';
import { showToast } from 'stores/ToastStore';
import client from './rest/AdminClient';
import AdminAppDispatcher from '../dispatcher/AdminAppDispatcher';
import AdminServerActionCreators from '../actions/AdminServerActionCreators';
import AdminConstants from '../constants/AdminConstants';
import { getBearerGrantTypeAuthHeaders } from '../helpers/auth_helpers';
import { attachDocuments } from './DocumentsAPIUtils';
const ActionTypes = AdminConstants.ActionTypes;
const ORDERS_CSV_FILENAME = 'orders.csv';
const exportCsvQueryOptions = {
    paginate: true,
    per_page: 1000,
};
function getDefaultQueryOptions() {
    return {
        populate: [
            {
                path: 'carrier',
                select: 'name _type',
            },
            {
                path: 'shipper',
                select: 'company_name _type',
            },
            {
                path: 'equipments',
                select: 'equipment_id _type',
            },
            {
                path: 'invoices',
                select: 'shipper_name type email_sent',
            },
        ],
        sort_by: '_id',
        order: 'desc',
    };
}
const getOrderAuditEvents = (id) => client({
    method: 'GET',
    path: `/api/audits/orders/${id}`,
});
const transitionOrder = (order, step, meta = {}) => {
    const payload = {
        meta,
        step,
    };
    return client({
        method: 'POST',
        path: `/admin/api/orders/${order._id}/transition`,
        entity: JSON.stringify(payload),
    });
};
const getOrders = (params) => {
    const query = _.extend(getDefaultQueryOptions(), params);
    const queryString = qs.stringify(query);
    return client({
        method: 'GET',
        path: `/api/orders?${queryString}`,
    });
};
export const searchOrdersByOrderId = (order_id, options) => {
    const query = Object.assign({ select: 'order_id status', order_id }, (options || {}));
    const queryString = qs.stringify(query);
    return client({
        method: 'GET',
        path: `/api/orders?${queryString}`,
    });
};
export const getCarrierScacs = () => {
    return client({
        method: 'GET',
        path: `/api/orders/meta/carrierscac`,
    });
};
const getOrderShipments = (orderId, options) => {
    const queryString = qs.stringify(Object.assign(Object.assign({}, options), { _: new Date().getTime() }));
    return client({
        method: 'GET',
        path: `/api/orders/${orderId}/shipments?${queryString}`,
    });
};
export const getOrderTemplate = async (id) => {
    try {
        const { entity: data } = await client({
            method: 'GET',
            path: `/api/order-templates/${id}`,
        });
        return data;
    }
    catch (e) {
        showToast(i18next.t('ORDER_TEMPLATE_FETCH_FAILED'), e.message, 'error');
    }
};
const getOrderTemplates = (params = {}) => {
    const query = Object.assign({}, params);
    const queryString = qs.stringify(query);
    return client({
        method: 'GET',
        path: `/api/order-templates?${queryString}`,
    });
};
const getOrder = (orderId) => client({
    method: 'GET',
    path: `/api/orders/${orderId}`,
});
const createOrder = (order) => client({
    method: 'POST',
    path: '/api/orders',
    entity: JSON.stringify(order),
});
const saveTemplateAndBookOrder = (id, name, order) => client({
    method: 'POST',
    path: '/api/order-templates/book',
    entity: JSON.stringify({
        id,
        name,
        order,
    }),
});
const createOrderAndTemplate = (name, order) => client({
    method: 'POST',
    path: '/api/order-templates/book',
    entity: JSON.stringify({
        name,
        order,
    }),
});
const createShipmentFromOrder = (order_id, options) => client({
    method: 'POST',
    path: `/admin/api/orders/${order_id}/shipment`,
    entity: JSON.stringify({
        options,
    }),
});
const cancelOrder = (orderId, reason) => {
    const params = {
        orderId,
        reason,
    };
    return client({
        method: 'POST',
        path: `/api/orders/${orderId}/cancel`,
        entity: JSON.stringify(params),
    });
};
export const addLeg = (orderId, payload) => {
    return client({
        method: 'POST',
        path: `/api/v3/orders/${orderId}/legs`,
        entity: JSON.stringify(payload),
    });
};
export const updateLeg = (shipmentId, payload) => {
    return client({
        method: 'PUT',
        path: `/api/v3/jobs/legs/${shipmentId}`,
        entity: JSON.stringify(payload),
    });
};
export const cancelLeg = (legId, reason, comment) => {
    return client({
        method: 'PATCH',
        path: `/api/v3/jobs/legs/${legId}/cancel`,
        entity: JSON.stringify({ reason, comment }),
    });
};
const reopenOrder = (orderId) => {
    return client({
        method: 'POST',
        path: `/api/orders/${orderId}/reopen`,
    });
};
export const updateOrder = (orderId, updates) => client({
    method: 'PATCH',
    path: `/api/orders/${orderId}`,
    entity: JSON.stringify(updates),
});
export const updateOrderDates = (orderId, updates) => client({
    method: 'PATCH',
    path: `/api/orders/${orderId}/dates`,
    entity: JSON.stringify(updates),
});
// Need to update
const uploadOrderDocument = (orderId, docType, files) => new Promise((resolve, reject) => {
    const req = request.post(`${API_HOST}/api/orders/${orderId}/upload`);
    files.forEach((file) => req.attach('file', file));
    req
        .set('Authorization', getBearerGrantTypeAuthHeaders())
        .field('order_id', orderId)
        .field('name', docType)
        .then((response) => {
        if (response.body && !response.body.success) {
            reject(new Error(response.body.message));
            return;
        }
        resolve(response);
    }, (err) => {
        reject(err);
    });
});
const deleteOrderDocument = (order, documentId) => {
    const documents = order.documents.map((doc) => {
        if (doc._id === documentId) {
            doc.show = false;
        }
        return doc;
    });
    return updateOrder(order._id, { documents: documents });
};
const addOrderComment = (orderId, message) => {
    return client({
        method: 'POST',
        path: `/api/orders/${orderId}/comments`,
        entity: JSON.stringify({
            message,
        }),
    });
};
const addEquipmentToOrder = (orderId, equipmentId) => {
    const params = {
        orderId,
        equipmentId,
    };
    return client({
        method: 'POST',
        path: `/api/orders/${orderId}/equipments`,
        entity: JSON.stringify(params),
    });
};
const removeEquipmentFromOrder = (orderId, equipmentId) => {
    const params = {
        orderId,
        equipmentId,
    };
    return client({
        method: 'DELETE',
        path: `/api/orders/${orderId}/equipments`,
        entity: JSON.stringify(params),
    });
};
const generateDeliveryOrder = (jobId) => {
    return client({
        method: 'POST',
        path: `/admin/api/v2/jobs/${jobId}/delivery-order`,
    });
};
const generateCustomDeliveryOrder = (orderId, payload) => {
    return client({
        method: 'POST',
        path: `/admin/api/orders/${orderId}/delivery-order/send`,
        entity: JSON.stringify(payload),
    });
};
const emailCustomDeliveryOrder = (orderId, documentId, addresses) => {
    return client({
        method: 'POST',
        path: `/admin/api/orders/${orderId}/delivery-order/${documentId}/send`,
        entity: JSON.stringify({
            addresses,
        }),
    });
};
export const getLegSummaries = (orderId) => {
    return client({
        method: 'GET',
        path: `/api/v3/jobs/${orderId}/legs/summary`,
    });
};
export const generateOrderInvoice = (orderId, invoiceDate, selectedCharges = {}, sendEdi = true, sendEmail = false, useShipmentAddress = false) => {
    const params = {
        orderId,
        invoiceDate,
        selectedCharges,
        sendEdi,
        sendEmail,
        useShipmentAddress,
    };
    return client({
        method: 'POST',
        path: `/api/orders/${orderId}/invoices`,
        entity: JSON.stringify(params),
    });
};
export const generateOrderPayout = (orderId, payoutDate, selectedCharges = {}, autoApprove = true) => {
    const params = {
        orderId,
        payoutDate,
        selectedCharges,
        autoApprove,
    };
    return client({
        method: 'POST',
        path: `/api/orders/${orderId}/payouts`,
        entity: JSON.stringify(params),
    });
};
export const generateCreditMemo = (orderId, invoiceId, invoiceDate, rebillInformation, selectedCharges = {}, rebill = true, sendEdi = true) => {
    const params = {
        orderId,
        invoiceId,
        invoiceDate,
        selectedCharges,
        rebill,
        sendEdi,
        rebillInformation,
    };
    return client({
        method: 'POST',
        path: `/api/orders/${orderId}/credit-memos`,
        entity: JSON.stringify(params),
    });
};
const downloadOrdersCsv = (filters, columns) => {
    const query = _.extend({}, filters, exportCsvQueryOptions);
    query.columns = columns;
    const queryString = qs.stringify(query);
    return client({
        method: 'POST',
        path: '/api/orders/csv-from-query?' + queryString,
    });
};
const bulkAddAccessorials = (orderId, updates) => {
    return client({
        method: 'POST',
        path: `/admin/api/orders/${orderId}/bulk/adjustments`,
        entity: JSON.stringify({ updates }),
    });
};
export const addHub = (shipmentId, yardId, hubbingType) => {
    return client({
        method: 'POST',
        path: `/api/v3/jobs/legs/${shipmentId}/hub`,
        entity: JSON.stringify({ yardId, hubbingType }),
    });
};
const API = {
    bulkAddAccessorials(orderId, updates) {
        bulkAddAccessorials(orderId, updates).then((res) => {
            const { order, successes, errors } = res.entity;
            AdminServerActionCreators.bulkAddAccessorialsSuccess({
                order,
                successes,
                errors,
            });
        }, () => {
            AdminServerActionCreators.bulkAddAccessorialsFail({
                errors: updates.map((s) => s._id),
            });
        });
    },
    getCarrierScacs() {
        return getCarrierScacs()
            .then((response) => {
            return response.entity;
        })
            .catch((err) => {
            console.log('Problem with scacs ', err);
        });
    },
    downloadOrdersCsv(filters, limit, activeColumns) {
        downloadOrdersCsv(filters, activeColumns).then((response) => {
            fileDownload(response.entity, ORDERS_CSV_FILENAME);
            AdminServerActionCreators.downloadOrdersCSVSuccess();
        });
    },
    getOrders(params) {
        getOrders(params).then((res) => {
            const { items: orders, pages, total_entries } = res.entity;
            AdminServerActionCreators.fetchOrdersSuccess({
                orders,
                pages,
                total_entries,
            });
        }, (err) => {
            AdminServerActionCreators.fetchOrdersFail(err.entity);
        });
    },
    getOrder(orderId) {
        getOrder(orderId).then((res) => {
            AdminServerActionCreators.fetchOrderSuccess(res.entity.order);
        }, (err) => {
            AdminServerActionCreators.fetchOrderFail(err.entity);
        });
    },
    createOrder(order, documentIds) {
        createOrder(order).then(async (res) => {
            AdminServerActionCreators.createOrderSuccess(res.entity.order);
            if (documentIds.length) {
                const result = await attachDocuments(documentIds, [
                    res.entity.order.id,
                ]);
                if (!result || result.errors) {
                    AdminServerActionCreators.createOrderFail(result.errors);
                }
            }
        }, (err) => {
            AdminServerActionCreators.createOrderFail(err.entity);
        });
    },
    createShipmentFromOrder(orderId, options) {
        createShipmentFromOrder(orderId, options).then((res) => AdminServerActionCreators.createShipmentFromOrderSuccess(res.entity.shipments), (err) => AdminServerActionCreators.createShipmentFromOrderFail(err.entity));
    },
    getOrderAuditEvents(id) {
        getOrderAuditEvents(id).then((response) => {
            AdminServerActionCreators.receiveOrderAuditSuccess(response.entity);
        }, (response) => {
            console.log('Failed to get event audits', response);
        });
    },
    saveTemplateAndBookOrder(id, name, order, documentIds) {
        saveTemplateAndBookOrder(id, name, order).then(async (res) => {
            AdminServerActionCreators.saveTemplateAndBookOrderSuccess(res.entity);
            if (documentIds.length) {
                const result = await attachDocuments(documentIds, [
                    res.entity.order.id,
                ]);
                if (!result || result.errors) {
                    AdminServerActionCreators.saveTemplateAndBookOrderFail(result.errors);
                }
            }
        }, (err) => AdminServerActionCreators.saveTemplateAndBookOrderFail(err.entity));
    },
    createOrderAndTemplate(name, order, documentIds) {
        createOrderAndTemplate(name, order).then(async (res) => {
            AdminServerActionCreators.createOrderAndTemplateSuccess(res.entity);
            if (documentIds.length) {
                const result = await attachDocuments(documentIds, [
                    res.entity.order.id,
                ]);
                if (!result || result.errors) {
                    AdminServerActionCreators.createOrderAndTemplateFail(result.errors);
                }
            }
        }, (err) => AdminServerActionCreators.createOrderAndTemplateFail(err.entity));
    },
    transitionOrder(order, step, meta) {
        transitionOrder(order, step, meta).then((res) => {
            AdminServerActionCreators.transitionOrderSuccess(res.entity.order);
        }, (err) => {
            AdminServerActionCreators.transitionOrderFail(err.entity);
        });
    },
    cancelOrder(params) {
        cancelOrder(params).then((res) => {
            AdminServerActionCreators.cancelOrderSuccess(res.entity.order);
        }, (err) => {
            AdminServerActionCreators.cancelOrderFail(err.entity);
        });
    },
    reopenOrder(orderId) {
        reopenOrder(orderId).then((res) => {
            AdminServerActionCreators.reopenOrderSuccess(res.entity.order);
        }, (err) => {
            AdminServerActionCreators.reopenOrderFail(err.entity);
        });
    },
    updateOrder(orderId, updates) {
        updateOrder(orderId, updates).then((res) => {
            AdminServerActionCreators.updateOrderSuccess(res.entity.order);
        }, (err) => {
            AdminServerActionCreators.updateOrderFail(err.entity);
        });
    },
    updateOrderDates(orderId, updates) {
        updateOrderDates(orderId, updates).then((res) => {
            AdminServerActionCreators.updateOrderSuccess(res.entity.order);
        }, (err) => {
            AdminServerActionCreators.updateOrderFail(err.entity);
        });
    },
    uploadOrderDocument(orderId, docType, file, meta) {
        uploadOrderDocument(orderId, docType, file, meta).then((res) => {
            AdminServerActionCreators.uploadOrderDocumentSuccess(res.body.order);
        }, (err) => {
            AdminServerActionCreators.uploadOrderDocumentFail({
                error: err.entity.message,
            });
        });
    },
    deleteOrderDocument(orderId, documentId) {
        deleteOrderDocument(orderId, documentId).then((res) => {
            AdminServerActionCreators.deleteOrderDocumentSuccess(res.entity.order);
        }, (err) => {
            AdminServerActionCreators.deleteOrderDocumentFail(err.entity);
        });
    },
    addOrderComment(orderId, comment) {
        addOrderComment(orderId, comment).then((res) => AdminServerActionCreators.addOrderCommentSuccess(res.entity.order), (res) => AdminServerActionCreators.addOrderCommentFail(res.entity));
    },
    addEquipmentToOrder(orderId, equipmentId) {
        addEquipmentToOrder(orderId, equipmentId).then((res) => {
            AdminServerActionCreators.addEquipmentToOrderSuccess(res.entity.order);
        }, (err) => {
            AdminServerActionCreators.addEquipmentToOrderFail({
                error: err.entity.message,
            });
        });
    },
    removeEquipmentFromOrder(orderId, equipmentId) {
        removeEquipmentFromOrder(orderId, equipmentId).then((res) => {
            AdminServerActionCreators.removeEquipmentFromOrderSuccess(res.entity.order);
        }, (err) => {
            AdminServerActionCreators.removeEquipmentFromOrderFail({
                error: err.entity.message,
            });
        });
    },
    generateDeliveryOrder(jobId) {
        generateDeliveryOrder(jobId).then((res) => {
            AdminServerActionCreators.generateDeliveryOrderSuccess(res.entity.response);
        }, (err) => {
            AdminServerActionCreators.generateDeliveryOrderFail({
                error: err.entity.error,
            });
        });
    },
    generateCustomDeliveryOrder(orderId, payload) {
        generateCustomDeliveryOrder(orderId, payload).then((res) => {
            AdminServerActionCreators.generateCustomDeliveryOrderSuccess(res.entity.order);
        }, (err) => {
            AdminServerActionCreators.generateCustomDeliveryOrderFail({
                error: err.entity.error,
            });
        });
    },
    emailCustomDeliveryOrder(orderId, documentId, addresses) {
        emailCustomDeliveryOrder(orderId, documentId, addresses).then((res) => {
            AdminServerActionCreators.emailCustomDeliveryOrderSuccess(res.entity.order);
        }, (err) => {
            AdminServerActionCreators.emailCustomDeliveryOrderFail({
                error: err.entity.message,
            });
        });
    },
    generateOrderInvoice(orderId, invoiceDate, selectedCharges, sendEdi, sendEmail) {
        generateOrderInvoice(orderId, invoiceDate, selectedCharges, sendEdi, sendEmail).then((res) => {
            const { invoice, order } = res.entity;
            AdminServerActionCreators.generateOrderInvoiceSuccess({
                invoice,
                order,
            });
        }, (err) => {
            AdminServerActionCreators.generateOrderInvoiceFail({
                error: err.entity.message,
            });
            showToast(i18next.t('GENERATE_INVOICE_FAILED'), i18next.t('GENERATE_INVOICE_FAILED_WITH_VALUE', {
                value: err.entity.message,
            }), 'error');
        });
    },
    generateOrderPayout(orderId, payoutDate, selectedCharges, autoApprove) {
        generateOrderPayout(orderId, payoutDate, selectedCharges, autoApprove).then((res) => {
            const { payout, order } = res.entity;
            AdminServerActionCreators.generateOrderPayoutSuccess({ payout, order });
        }, (err) => {
            AdminServerActionCreators.generateOrderPayoutFail({
                error: err.entity.message,
            });
            showToast(i18next.t('GENERATE_PAYOUT_FAILED'), i18next.t('GENERATE_PAYOUT_FAILED_WITH_VALUE', {
                value: err.entity.message,
            }), 'error');
        });
    },
    searchOrdersByOrderId(orderId, options) {
        searchOrdersByOrderId(orderId, options).then((response) => AdminServerActionCreators.getOrdersByQuerySearchSuccess(response), (response) => AdminServerActionCreators.getOrdersByQuerySearchFail(response));
    },
    getOrdersByQuery(query) {
        getOrders(query).then((response) => AdminServerActionCreators.getOrdersByQuerySearchSuccess(response), (response) => AdminServerActionCreators.getOrdersByQuerySearchFail(response));
    },
    getOrderShipments(orderId, options) {
        getOrderShipments(orderId, options).then((response) => AdminServerActionCreators.getOrderShipmentsSuccess(response.entity.jobs), (response) => AdminServerActionCreators.getOrderShipmentsFail(response.entity));
    },
    getOrderTemplates(query = {}) {
        getOrderTemplates(query).then((response) => AdminServerActionCreators.getOrderTemplatesSuccess(response.entity.templates), (response) => AdminServerActionCreators.getOrderTemplatesFail(response.entity.message));
    },
};
AdminAppDispatcher.register((payload) => {
    const action = payload.action || {};
    const type = action.type;
    const params = action.params || {};
    switch (type) {
        case ActionTypes.FETCH_ORDER_TEMPLATES_REQUEST:
            API.getOrderTemplates(params);
            break;
        case ActionTypes.FETCH_ORDERS_REQUEST:
            API.getOrders(params);
            break;
        case ActionTypes.FETCH_ORDER_REQUEST:
            API.getOrder(action.orderId);
            break;
        case ActionTypes.TRANSITION_ORDER_REQUEST:
            API.transitionOrder(action.order, action.step, action.meta);
            break;
        case ActionTypes.CREATE_ORDER_REQUEST:
            API.createOrder(action.order, action.documentIds);
            break;
        case ActionTypes.SAVE_TEMPLATE_AND_BOOK_ORDER_REQUEST:
            API.saveTemplateAndBookOrder(action.id, action.name, action.order, action.documentIds);
            break;
        case ActionTypes.CREATE_ORDER_AND_TEMPLATE_REQUEST:
            API.createOrderAndTemplate(action.name, action.order, action.documentIds);
            break;
        case ActionTypes.CREATE_SHIPMENT_FROM_ORDER_REQUEST:
            API.createShipmentFromOrder(action.orderId, action.options);
            break;
        case ActionTypes.CANCEL_ORDER_REQUEST:
            API.cancelOrder(action.orderId, action.reason);
            break;
        case ActionTypes.REOPEN_ORDER_REQUEST:
            API.reopenOrder(action.orderId);
            break;
        case ActionTypes.UPDATE_ORDER_REQUEST:
            API.updateOrder(action.orderId, action.updates);
            break;
        case ActionTypes.UPDATE_ORDER_DATES:
            API.updateOrderDates(action.orderId, action.updates);
            break;
        case ActionTypes.UPLOAD_ORDER_DOCUMENT_REQUEST:
            API.uploadOrderDocument(action.orderId, action.docType, action.file, action.meta);
            break;
        case ActionTypes.DELETE_ORDER_DOCUMENT_REQUEST:
            API.deleteOrderDocument(action.order, action.documentId);
            break;
        case ActionTypes.ADD_ORDER_COMMENT_REQUEST:
            API.addOrderComment(action.orderId, action.comment);
            break;
        case ActionTypes.ADD_ORDER_EQUIPMENT_REQUEST:
            API.addEquipmentToOrder(action.orderId, action.equipmentId);
            break;
        case ActionTypes.REMOVE_ORDER_EQUIPMENT_REQUEST:
            API.removeEquipmentFromOrder(action.orderId, action.equipmentId);
            break;
        case ActionTypes.GENERATE_ORDER_INVOICE_REQUEST:
            API.generateOrderInvoice(action.orderId, action.invoiceDate, action.selectedCharges, action.sendEdi, action.sendEmail, action.useShipmentAddress);
            break;
        case ActionTypes.GENERATE_ORDER_PAYOUT_REQUEST:
            API.generateOrderPayout(action.orderId, action.payoutDate, action.selectedCharges, action.autoApprove);
            break;
        case ActionTypes.DOWNLOAD_ORDERS_CSV:
            API.downloadOrdersCsv(action.filters, action.limit, action.activeColumns);
            break;
        case ActionTypes.BULK_ADD_ACCESSORIALS_REQUEST:
            API.bulkAddAccessorials(action.orderId, action.shipments);
            break;
        case ActionTypes.ORDERS_SEARCH_BY_QUERY_REQUEST:
            API.getOrdersByQuery(action.query);
            break;
        case ActionTypes.GENERATE_DELIVERY_ORDER_REQUEST:
            API.generateDeliveryOrder(action.jobId);
            break;
        case ActionTypes.GENERATE_CUSTOM_DELIVERY_ORDER_REQUEST:
            API.generateCustomDeliveryOrder(action.orderId, action.payload);
            break;
        case ActionTypes.EMAIL_CUSTOM_DELIVERY_ORDER_REQUEST:
            API.emailCustomDeliveryOrder(action.orderId, action.documentId, action.addresses);
            break;
        case ActionTypes.FETCH_ORDER_SHIPMENTS_REQUEST:
            API.getOrderShipments(action.orderId, action.options);
            break;
        default:
            break;
    }
});
export default API;
